import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { authStore } from "@/store/useAuthStore";

import { getMe, postLogin, postLogout } from "../api/authApi";

// useBootstrapAuthQuery / usePostLogin / useLogout 이 같은 캐시 항목을 가리켜야 하므로
// 하나의 상수로 공유한다.
const ME_QUERY_KEY = ["auth", "me"] as const;

export const usePostLogin = () => {
  const setAuthData = authStore((state) => state.setAuthData);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: postLogin,
    onSuccess: (data) => {
      // 이전 세션(다른 계정)의 /me/ 응답이 staleTime(5분) 안에 남아 있으면
      // useBootstrapAuthQuery 가 새 로그인 직후 이 캐시를 그대로 authStore.user 에
      // 덮어써 버린다 — 로그아웃 후 다른 계정으로 로그인해도 이전 계정이 보이는 원인.
      // 새 세션을 세우기 전에 이전 캐시를 지워, 재활성화된 쿼리가 새 토큰으로 다시
      // 조회하도록 한다.
      queryClient.removeQueries({ queryKey: ME_QUERY_KEY });
      setAuthData(data);
    },
    onError: (error: unknown) => {
      console.error("로그인 실패", error);
    },
  });
};

export const useLogout = () => {
  const navigate = useNavigate();
  const refreshToken = authStore((state) => state.refreshToken);
  const clearAuth = authStore((state) => state.clearAuth);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // 로컬 세션은 네트워크 왕복을 기다리지 않고 즉시 지운다. 백엔드 호출까지 기다리면
      // 그 사이 다른 요청이 401 을 받아 axios 인터셉터가 refresh 를 시도할 수 있고,
      // 그 refresh 가 이 로그아웃보다 늦게 끝나면 store 에 새 토큰이 다시 채워진다
      // (accessToken/refreshToken 이 "로그아웃 후" 되살아나는 것처럼 보이는 원인).
      // refreshToken 이 먼저 null 이 되면 axios.ts 의 refreshAccessToken 이 즉시
      // null 을 반환하므로 이 경쟁이 성립하지 않는다.
      const tokenToRevoke = refreshToken;
      clearAuth();
      // 다음 로그인(다른 계정일 수 있음)이 이 계정의 캐시된 /me/ 응답을 물려받지
      // 않도록 함께 지운다.
      queryClient.removeQueries({ queryKey: ME_QUERY_KEY });
      navigate("/login", { replace: true });

      if (tokenToRevoke) {
        try {
          await postLogout(tokenToRevoke);
        } catch {
          // 서버 측 블랙리스트 실패는 무시 — 로컬 세션은 이미 종료됐다.
        }
      }
    },
  });
};

// 앱 부트스트랩 시 저장된 토큰의 유효성 확인 및 사용자 동기화
export const useBootstrapAuthQuery = () => {
  const accessToken = authStore((state) => state.accessToken);
  const setUser = authStore((state) => state.setUser);
  const clearAuth = authStore((state) => state.clearAuth);

  const query = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: getMe,
    enabled: !!accessToken,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (query.data) {
      setUser(query.data);
    }
  }, [query.data, setUser]);

  useEffect(() => {
    if (query.isError) {
      clearAuth();
    }
  }, [query.isError, clearAuth]);

  return query;
};
